/**
 * Compliance landing page — admin / compliance / read_only.
 *
 * Tabbed view (Phase E1):
 *   1. Overview        — maker-checker queue rollup
 *   2. Review intervals — interval rules (AML / MiFID / KYC)
 *   3. Completeness    — required-field rules + incomplete-records list
 *   4. Expiring dates  — Phase E2 placeholder
 */
import {
  ActionIcon, Anchor, Badge, Button, Container, Fieldset, Group,
  Modal, NumberInput, Paper, Select, Stack, Switch, Table, Tabs, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconAlertTriangle, IconCalendarTime, IconChartHistogram,
  IconClipboardCheck, IconClipboardList, IconEdit, IconHistory,
  IconList, IconPlus, IconScale, IconShieldCheck,
  IconTrash,
} from '@tabler/icons-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ComplianceRulesSection } from '../components/ComplianceRulesSection';
import { RequireAccess } from '../components/RequireAccess';
import type { ComplianceScope } from '../api/completeness';
import type { UserRole } from '../api/types';
import { MetricCard } from '../components/MetricCard';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { useAlertModal } from '../components/AlertModalProvider';
import { useChangeRequestsList } from '../hooks/useChangeRequests';
import { useCurrentUser } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { EXPIRING_KIND_LABEL } from '../utils/expiringLabels';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import {
  useCompletenessRules, useCreateCompletenessRule, useDeleteCompletenessRule,
  useExpiring, useIncompleteRecords, useUpdateCompletenessRule,
} from '../hooks/useCompleteness';
import type {
  CompletenessEntityType, CompletenessRule, ExpiringDateInfo,
  ExpiringDateKind, ExpiringRecord, IncompleteRecord,
} from '../api/types';
import { conditionDimensionsFor, conditionValuesFor, fieldOptionsForSelect } from '../api/fieldRegistry';
import { ApiError } from '../api/client';
import { SortableTh, nextSort, sortRows, type SortState } from '../components/SortableHeader';

const ENTITY_TYPE_LABEL: Record<CompletenessEntityType, string> = {
  AGREEMENT: 'Agreement',
  PERSON: 'Person',
  ACCOUNT: 'Account',
  INSURANCE_POLICY: 'Policy',
};
const ENTITY_TYPE_OPTS = (Object.keys(ENTITY_TYPE_LABEL) as CompletenessEntityType[]).map(
  (v) => ({ value: v, label: ENTITY_TYPE_LABEL[v] }),
);

// MetricCard moved to components/MetricCard.tsx (shared with AdminPage, 2026-06-21).

/** C-356 (Pedro 2026-10-11): Admin and Compliance only — the sidebar hid it from other
 *  roles, but the page itself was open to anyone who followed a link or typed the URL.
 *  RM / Staff / read_only see their own follow-ups on /my-follow-ups. */
export const COMPLIANCE_PAGE_ROLES: UserRole[] = ['admin', 'compliance'];

export function CompliancePage() {
  return (
    <RequireAccess roles={COMPLIANCE_PAGE_ROLES}
      message="This area is available to Admin and Compliance only.">
      <CompliancePageBody />
    </RequireAccess>
  );
}

function CompliancePageBody() {
  useDocumentTitle('Compliance');
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') ?? 'overview';
  const setActiveTab = (v: string | null) => {
    if (!v) return;
    const next = new URLSearchParams(searchParams);
    if (v === 'overview') next.delete('tab'); else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>Compliance</Title>
        <Text size="sm" c="dimmed">
          Maker-checker queue, review-cadence rules, and completeness checks.
        </Text>

        <Tabs value={urlTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconClipboardCheck size={14} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="intervals" leftSection={<IconScale size={14} />}>
              Review intervals
            </Tabs.Tab>
            <Tabs.Tab value="completeness-rules" leftSection={<IconClipboardList size={14} />}>
              Completeness rules
            </Tabs.Tab>
            <Tabs.Tab value="incomplete" leftSection={<IconList size={14} />}>
              Incomplete records
            </Tabs.Tab>
            <Tabs.Tab value="expiring" leftSection={<IconCalendarTime size={14} />}>
              Expiring dates
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <OverviewTab />
          </Tabs.Panel>
          <Tabs.Panel value="intervals" pt="md">
            <ComplianceRulesSection />
          </Tabs.Panel>
          <Tabs.Panel value="completeness-rules" pt="md">
            <CompletenessRulesSection />
          </Tabs.Panel>
          <Tabs.Panel value="incomplete" pt="md">
            <IncompleteRecordsSection />
          </Tabs.Panel>
          <Tabs.Panel value="expiring" pt="md">
            <ExpiringDatesSection />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}


// ─── Overview tab ────────────────────────────────────────────────────────
function OverviewTab() {
  const pending = useChangeRequestsList('PENDING');
  const applied = useChangeRequestsList('APPLIED');
  const rejected = useChangeRequestsList('REJECTED');
  const incomplete = useIncompleteRecords();
  const { user } = useCurrentUser();
  const isAdmin = user?.role === 'admin';
  return (
    <Stack>
      <Group grow align="stretch" wrap="wrap">
        <MetricCard
          title="Pending change requests"
          value={pending.isLoading ? '…' : (pending.data?.total ?? 0)}
          subtitle="Awaiting your decision"
          color={pending.data?.total ? 'laranja' : 'azul'}
          icon={IconClipboardCheck}
          to="/change-requests"
        />
        <MetricCard
          title="Applied this cycle"
          value={applied.isLoading ? '…' : (applied.data?.total ?? 0)}
          subtitle="Approved + applied"
          color="verde"
          icon={IconShieldCheck}
          to="/change-requests"
        />
        <MetricCard
          title="Rejected"
          value={rejected.isLoading ? '…' : (rejected.data?.total ?? 0)}
          subtitle="Returned to maker"
          color="bordeaux"
          icon={IconAlertTriangle}
          to="/change-requests"
        />
        <MetricCard
          title="Incomplete records"
          value={incomplete.isLoading ? '…' : (incomplete.data?.total ?? 0)}
          subtitle="Failing one or more rules"
          color={incomplete.data?.total ? 'laranja' : 'azul'}
          icon={IconAlertCircle}
          to="/compliance?tab=incomplete"
        />
      </Group>

      {/* Audit log + Data extraction stay on Compliance; the Users / Permissions /
          SAA / Standard-fees / Compute-fees cards moved to the Admin page
          (2026-06-21 nav reorg). Each kept card keeps its prior gating + route. */}
      <Group grow align="stretch" wrap="wrap">
        <MetricCard
          title="Audit log"
          subtitle="Audit trail + CSV export"
          color="azul"
          icon={IconHistory}
          to="/audit"
        />
        {isAdmin && (
          <MetricCard
            title="Data extraction"
            subtitle="DLP extraction dashboard"
            color="azul"
            icon={IconChartHistogram}
            to="/admin/extraction"
          />
        )}
      </Group>
    </Stack>
  );
}


// ─── Completeness rules tab ──────────────────────────────────────────────
function CompletenessRulesSection() {
  const [filterType, setFilterType] = React.useState<CompletenessEntityType | ''>('');
  const rulesQ = useCompletenessRules(filterType || undefined);
  const create = useCreateCompletenessRule();
  const del = useDeleteCompletenessRule();
  const confirmDelete = useConfirmDelete();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CompletenessRule | null>(null);
  // Phase E1 follow-up: only admin / compliance can mutate rules. Other
  // roles (read_only, RM, staff) see the table but no edit affordances.
  const { can } = usePermissions();
  const canEdit = can('completeness_rule.manage');
  const { showAlert } = useAlertModal();

  return (
    <Fieldset legend={
      <Group gap="xs"><IconClipboardList size={16} />
        <span>Completeness rules — required fields per entity type</span>
      </Group>
    }>
      <Stack>
        <Text size="sm" c="dimmed">
          Each rule says "for entities of this type matching `applies_when`,
          this field must not be empty". Empty `applies_when` means the rule
          applies to every record of the type.
        </Text>
        <Group justify="space-between">
          <Select
            placeholder="All entity types"
            data={[{ value: '', label: 'All entity types' }, ...ENTITY_TYPE_OPTS]}
            value={filterType}
            onChange={(v) => setFilterType((v as CompletenessEntityType | '') || '')}
            allowDeselect={false}
            style={{ width: 220 }}
          />
          {canEdit && (
            <Button leftSection={<IconPlus size={14} />} size="xs"
              onClick={() => setCreateOpen(true)}>
              Add rule
            </Button>
          )}
        </Group>

        {rulesQ.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
        {(rulesQ.data?.items.length ?? 0) === 0 && (
          <Text size="sm" c="dimmed">No rules — add the first one.</Text>
        )}
        {(rulesQ.data?.items.length ?? 0) > 0 && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={130}>Entity</Table.Th>
                <Table.Th w={220}>Applies when</Table.Th>
                <Table.Th w={220}>Field</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th w={60} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rulesQ.data!.items.map((r) => (
                <CompletenessRuleRow key={r.id} rule={r}
                  canEdit={canEdit}
                  onEdit={() => setEditing(r)}
                  onDelete={() => confirmDelete('completeness rule', () => del.mutate(r.id))} />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {editing && (
        <EditRuleModal
          rule={editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Modal opened={createOpen} onClose={() => setCreateOpen(false)}
        title="Add completeness rule" size="md" centered>
        <CreateRuleForm
          onCancel={() => setCreateOpen(false)}
          onSubmit={(input) => {
            create.mutate(input, {
              onSuccess: () => {
                notifications.show({ color: 'green', message: 'Rule added' });
                setCreateOpen(false);
              },
              // #192 polish 2026-06-08: save error inside a Modal — Edge
              // case 6 (modal-stays-modal). showAlert renders a second
              // modal over the form modal so the error is visible in the
              // user's current context, not as a top-right toast.
              onError: (err) => {
                showAlert({
                  color: 'red',
                  title: 'Could not add rule',
                  message: err instanceof ApiError ? err.detail : 'Unknown error',
                });
              },
            });
          }}
          submitting={create.isPending}
        />
      </Modal>
    </Fieldset>
  );
}

function CompletenessRuleRow({ rule, canEdit, onEdit, onDelete }: {
  rule: CompletenessRule;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const aw = Object.entries(rule.applies_when ?? {});
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <Badge variant="light">{ENTITY_TYPE_LABEL[rule.entity_type]}</Badge>
          {/* B1/#35 (2026-06-11, old #364): pending-CR badge — rule edits
              ride maker-checker since 2026-06-08; show the filed state. */}
          <PendingCRBadge crs={rule.pending_change_requests} />
        </Group>
      </Table.Td>
      <Table.Td>
        {aw.length === 0
          ? <Text size="xs" c="dimmed">always</Text>
          : (
            <Stack gap={2}>
              {aw.map(([k, v]) => (
                <Text key={k} size="xs" ff="monospace">{k} = {String(v)}</Text>
              ))}
            </Stack>
          )}
      </Table.Td>
      <Table.Td><Text size="sm" ff="monospace">{rule.field_name}</Text></Table.Td>
      <Table.Td><Text size="sm" c="dimmed">{rule.description ?? '—'}</Text></Table.Td>
      <Table.Td>
        {canEdit && (
          <Group gap={4} wrap="nowrap">
            <ActionIcon variant="subtle" color="azul" size="sm" onClick={onEdit}
              title="Edit description">
              <IconEdit size={14} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red" size="sm" onClick={onDelete}
              title="Delete">
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

interface CreateRuleFormValues {
  entity_type: CompletenessEntityType;
  applies_when_key: string;
  applies_when_value: string;
  field_name: string;
  description: string;
}

function CreateRuleForm({
  onSubmit, onCancel, submitting,
}: {
  onSubmit: (input: {
    entity_type: CompletenessEntityType;
    applies_when?: Record<string, string>;
    field_name: string;
    description?: string | null;
  }) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const form = useForm<CreateRuleFormValues>({
    initialValues: {
      entity_type: 'AGREEMENT',
      applies_when_key: '',
      applies_when_value: '',
      field_name: '',
      description: '',
    },
    validate: {
      field_name: (v) => v.trim() === '' ? 'Required' : null,
      applies_when_value: (v, all) =>
        all.applies_when_key && !v
          ? 'Pick a value or clear the condition'
          : null,
    },
  });

  // Mantine grouped Select wants the options in the shape
  // [{ group: 'Foo', items: [{value, label}, ...] }, ...]. Reactively pick
  // the field set when the user changes entity type, and clear field_name
  // if it no longer belongs to the new set.
  const groupedFieldOpts = React.useMemo(
    () => fieldOptionsForSelect(form.values.entity_type),
    [form.values.entity_type],
  );
  React.useEffect(() => {
    const allValues = groupedFieldOpts.flatMap((g) => g.items.map((i) => i.value));
    if (form.values.field_name && !allValues.includes(form.values.field_name)) {
      form.setFieldValue('field_name', '');
    }
    // Same hygiene for condition key / value: clear them if they don't
    // belong to the new entity type's dimensions.
    const dims = conditionDimensionsFor(form.values.entity_type);
    if (form.values.applies_when_key && !dims.find((d) => d.key === form.values.applies_when_key)) {
      form.setFieldValue('applies_when_key', '');
      form.setFieldValue('applies_when_value', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.entity_type]);

  // Dropdown options for the two condition-* selects.
  const conditionKeyOpts = React.useMemo(() => {
    const dims = conditionDimensionsFor(form.values.entity_type);
    return [
      { value: '', label: '— always (no condition)' },
      ...dims.map((d) => ({ value: d.key, label: `${d.label} (${d.key})` })),
    ];
  }, [form.values.entity_type]);

  const conditionValueOpts = React.useMemo(() => {
    if (!form.values.applies_when_key) return [];
    return conditionValuesFor(form.values.entity_type, form.values.applies_when_key) ?? [];
  }, [form.values.entity_type, form.values.applies_when_key]);

  // When the key changes, clear the value (it likely no longer makes sense).
  React.useEffect(() => {
    if (!form.values.applies_when_key) {
      if (form.values.applies_when_value) form.setFieldValue('applies_when_value', '');
      return;
    }
    const legal = conditionValueOpts.map((v) => v.value);
    if (form.values.applies_when_value && !legal.includes(form.values.applies_when_value)) {
      form.setFieldValue('applies_when_value', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.applies_when_key]);

  return (
    <form onSubmit={form.onSubmit((values) => {
      const aw: Record<string, string> = {};
      if (values.applies_when_key && values.applies_when_value) {
        aw[values.applies_when_key] = values.applies_when_value;
      }
      onSubmit({
        entity_type: values.entity_type,
        applies_when: aw,
        field_name: values.field_name,
        description: values.description || null,
      });
    })}>
      <Stack>
        <Select label="Entity type" data={ENTITY_TYPE_OPTS} required
          allowDeselect={false}
          value={form.values.entity_type}
          onChange={(v) => {
            if (!v) return;
            // Atomic reset: switching entity type clears the field +
            // condition because the legal options for both depend on it.
            form.setValues({
              ...form.values,
              entity_type: v as CompletenessEntityType,
              applies_when_key: '',
              applies_when_value: '',
              field_name: '',
            });
          }}
        />
        <Group grow>
          <Select
            label="Condition (optional)"
            data={conditionKeyOpts}
            allowDeselect={false}
            value={form.values.applies_when_key}
            error={form.errors.applies_when_key as string | undefined}
            onChange={(v) => {
              // Atomic reset: changing the key clears the previously
              // chosen value (it likely no longer makes sense).
              form.setValues({
                ...form.values,
                applies_when_key: v ?? '',
                applies_when_value: '',
              });
            }}
          />
          <Select
            // The `key` ties the rendered Select to the current condition
            // key. When the user changes the key, React unmounts/remounts
            // this Select so its internal display string can't survive
            // and show stale text.
            key={`condval-${form.values.entity_type}-${form.values.applies_when_key}`}
            label="Condition value"
            placeholder={form.values.applies_when_key ? 'Pick a value' : '— pick a condition first —'}
            data={conditionValueOpts}
            disabled={!form.values.applies_when_key}
            allowDeselect={false}
            value={form.values.applies_when_value || null}
            error={form.errors.applies_when_value as string | undefined}
            onChange={(v) => form.setFieldValue('applies_when_value', v ?? '')}
          />
        </Group>
        <Text size="xs" c="dimmed">
          Leave the condition empty for "always". Picking a condition
          restricts the rule to records where the chosen attribute matches
          the chosen value.
        </Text>
        <Select
          // Same remount trick: keyed on entity_type so the Select rebuilds
          // when the field set changes (e.g. switching Agreement → Person).
          key={`field-${form.values.entity_type}`}
          label="Field"
          required
          placeholder="Pick a field"
          searchable
          nothingFoundMessage="No matching field"
          data={groupedFieldOpts}
          value={form.values.field_name || null}
          error={form.errors.field_name as string | undefined}
          onChange={(v) => form.setFieldValue('field_name', v ?? '')}
        />
        <TextInput label="Description (shown to users)"
          {...form.getInputProps('description')} />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel}>Cancel</Button>
          <Button type="submit" loading={submitting}>Add rule</Button>
        </Group>
      </Stack>
    </form>
  );
}


function EditRuleModal({ rule, onClose }: {
  rule: CompletenessRule;
  onClose: () => void;
}) {
  const [description, setDescription] = React.useState(rule.description ?? '');
  const update = useUpdateCompletenessRule(rule.id);
  const dirty = description !== (rule.description ?? '');
  const { showAlert } = useAlertModal();

  return (
    <Modal opened onClose={onClose} title="Edit completeness rule"
      size="md" centered>
      <Stack>
        {/* Identity (entity / condition / field) is immutable — it
            uniquely identifies the rule. To change any of those, delete
            and recreate. */}
        <Group gap="sm" wrap="wrap">
          <Badge variant="light">{ENTITY_TYPE_LABEL[rule.entity_type]}</Badge>
          {Object.entries(rule.applies_when ?? {}).length > 0 ? (
            Object.entries(rule.applies_when ?? {}).map(([k, v]) => (
              <Badge key={k} variant="light" color="gray">
                {k} = {String(v)}
              </Badge>
            ))
          ) : (
            <Badge variant="light" color="gray">always</Badge>
          )}
          <Badge variant="filled" color="azul">{rule.field_name}</Badge>
        </Group>
        <Text size="xs" c="dimmed">
          Identity (entity / condition / field) is fixed once a rule
          exists. To change them, delete this rule and add a new one.
        </Text>
        <TextInput
          label="Description (shown to users)"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!dirty}
            loading={update.isPending}
            onClick={() => {
              update.mutate({ description: description || null }, {
                onSuccess: () => {
                  notifications.show({ color: 'green', message: 'Rule updated' });
                  onClose();
                },
                // #192 polish 2026-06-08: save error inside a Modal — Edge
                // case 6 (modal-stays-modal). showAlert renders a second
                // modal over the form modal so the error is visible in the
                // user's current context, not as a top-right toast.
                onError: (err) => {
                  showAlert({
                    color: 'red',
                    title: 'Could not update rule',
                    message: err instanceof ApiError ? err.detail : 'Unknown error',
                  });
                },
              });
            }}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}


// ─── Incomplete records tab ──────────────────────────────────────────────
/** Also the "Incomplete records" tab of My follow-ups (C-356), with scope 'mine'. */
export function IncompleteRecordsSection({ scope = 'all' }: { scope?: ComplianceScope } = {}) {
  const [filterType, setFilterType] = React.useState<CompletenessEntityType | ''>('');
  // Pedro 2026-05-06: default OFF — Compliance Officers' day-to-day worklist
  // excludes findings on entities tied only to Inactivo agreements. Toggle
  // ON to see the full historical view (data preserved either way).
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const navigate = useNavigate();
  const reportQ = useIncompleteRecords(filterType || undefined, includeInactive, scope);
  // P41 phase 2 (2026-10-15): header sort. The report returns the whole set, so it sorts
  // client-side; null = the server's order. "Missing fields" sorts by how many are missing.
  const [sort, setSort] = React.useState<SortState<'type' | 'record' | 'missing'> | null>(null);
  const items = React.useMemo(() => sortRows(reportQ.data?.items ?? [], sort, (r, k) =>
    k === 'type' ? ENTITY_TYPE_LABEL[r.entity_type]
      : k === 'record' ? r.label : r.missing_fields.length), [reportQ.data, sort]);

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <Group gap="md">
          <Text size="sm" c="dimmed">
            {reportQ.isLoading
              ? 'Scanning…'
              : `${reportQ.data?.total ?? 0} record${(reportQ.data?.total ?? 0) === 1 ? '' : 's'} with at least one missing required field.`}
          </Text>
          {reportQ.data && (
            <Group gap={4}>
              {(['AGREEMENT', 'PERSON', 'ACCOUNT', 'INSURANCE_POLICY'] as CompletenessEntityType[]).map((et) => {
                const n = reportQ.data!.by_entity_type[et] ?? 0;
                if (!n) return null;
                return (
                  <Badge key={et} variant="light" color="laranja">
                    {ENTITY_TYPE_LABEL[et]}: {n}
                  </Badge>
                );
              })}
            </Group>
          )}
        </Group>
        <Group gap="md">
          <Switch
            label="Include inactive"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
            size="sm"
          />
          <Select
            placeholder="All entity types"
            data={[{ value: '', label: 'All entity types' }, ...ENTITY_TYPE_OPTS]}
            value={filterType}
            onChange={(v) => setFilterType((v as CompletenessEntityType | '') || '')}
            allowDeselect={false}
            style={{ width: 220 }}
          />
        </Group>
      </Group>

      {reportQ.data && reportQ.data.items.length === 0 && (
        <Paper withBorder p="lg" radius="md">
          <Group gap="sm">
            <IconShieldCheck size={20} color="var(--mantine-color-verde-6)" />
            <Text size="sm">All records satisfy the active completeness rules.</Text>
          </Group>
        </Paper>
      )}

      {reportQ.data && reportQ.data.items.length > 0 && (
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <SortableTh label="Type" col="type" sort={sort} w={120}
                onSort={(k) => setSort((s) => nextSort(s, k))} filtered={filterType !== ''} />
              <SortableTh label="Record" col="record" sort={sort}
                onSort={(k) => setSort((s) => nextSort(s, k))} />
              <SortableTh label="Missing fields" col="missing" sort={sort}
                onSort={(k) => setSort((s) => nextSort(s, k))} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((r) => (
              <IncompleteRow key={`${r.entity_type}-${r.entity_id}`} record={r}
                onClick={() => navigate(r.detail_path)} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function IncompleteRow({
  record, onClick,
}: { record: IncompleteRecord; onClick: () => void }) {
  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={onClick}>
      <Table.Td>
        <Badge variant="light">{ENTITY_TYPE_LABEL[record.entity_type]}</Badge>
      </Table.Td>
      <Table.Td><Text fw={500}>{record.label}</Text></Table.Td>
      <Table.Td>
        <Group gap={4} wrap="wrap">
          {record.missing_fields.map((f, i) => (
            <Tooltip key={f}
              label={record.explanations[i] ?? f} withArrow openDelay={200}>
              <Badge size="sm" variant="light" color="laranja">{f}</Badge>
            </Tooltip>
          ))}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}


// ─── Expiring dates tab (Phase E2) ───────────────────────────────────────
// The kind → label map is shared with the Dashboard "Next 30 days" card (P11).
const DATE_KIND_LABEL: Record<ExpiringDateKind, string> = EXPIRING_KIND_LABEL;

function statusBadge(daysUntilDue: number) {
  // Task #143 (2026-06-02): cursor:pointer on each variant — these badges
  // sit inside the clickable ExpiringDatePill anchor; Mantine Badge's
  // default cursor would otherwise override the anchor's pointer cursor
  // when hovering directly over the badge.
  const cursorStyles = { root: { cursor: 'pointer' } };
  if (daysUntilDue < 0) {
    return (
      <Badge size="xs" color="bordeaux" variant="filled" styles={cursorStyles}>
        Overdue {Math.abs(daysUntilDue)}d
      </Badge>
    );
  }
  if (daysUntilDue === 0) {
    return <Badge size="xs" color="laranja" variant="filled" styles={cursorStyles}>Due today</Badge>;
  }
  return (
    <Badge size="xs" color="laranja" variant="light" styles={cursorStyles}>
      Due in {daysUntilDue}d
    </Badge>
  );
}

/** Also the "Expiring dates" tab of My follow-ups (C-356), with scope 'mine'. */
export function ExpiringDatesSection({ scope = 'all' }: { scope?: ComplianceScope } = {}) {
  const navigate = useNavigate();
  const [withinMonths, setWithinMonths] = React.useState<number>(3);
  // Client-side filter: backend returns Agreements + Persons together;
  // toggling here trims the list without a re-fetch.
  const [filterType, setFilterType] = React.useState<CompletenessEntityType | ''>('');
  // Pedro 2026-05-06: same toggle as Incomplete Records — default OFF for
  // day-to-day worklist. Backend filters at the DB query layer.
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const reportQ = useExpiring(withinMonths, includeInactive, scope);

  const filteredItems = React.useMemo(() => {
    if (!reportQ.data) return [];
    if (!filterType) return reportQ.data.items;
    return reportQ.data.items.filter((r) => r.entity_type === filterType);
  }, [reportQ.data, filterType]);
  // P41 phase 2 (2026-10-15): header sort, client-side over the whole report; null = the
  // server's order. "Expiring dates" sorts by the record's soonest date.
  const [sort, setSort] = React.useState<SortState<'type' | 'record' | 'dates'> | null>(null);
  const sortedItems = React.useMemo(() => sortRows(filteredItems, sort, (r, k) =>
    k === 'type' ? ENTITY_TYPE_LABEL[r.entity_type]
      : k === 'record' ? r.label
        : r.dates.length ? Math.min(...r.dates.map((d) => d.days_until_due)) : null),
  [filteredItems, sort]);

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <Text size="sm" c="dimmed">
          {reportQ.isLoading
            ? 'Scanning…'
            : `${filteredItems.length} record${filteredItems.length === 1 ? '' : 's'} with one or more dates overdue or expiring within ${withinMonths} month${withinMonths === 1 ? '' : 's'}.`}
        </Text>
        <Group gap="sm">
          <Switch
            label="Include inactive"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
            size="sm"
          />
          <Select
            placeholder="All entity types"
            data={[{ value: '', label: 'All entity types' }, ...ENTITY_TYPE_OPTS]}
            value={filterType}
            onChange={(v) => setFilterType((v as CompletenessEntityType | '') || '')}
            allowDeselect={false}
            style={{ width: 200 }}
          />
          <Text size="sm">Window:</Text>
          <NumberInput
            value={withinMonths}
            onChange={(v) => {
              const n = typeof v === 'number' ? v : parseInt(String(v), 10);
              if (!Number.isNaN(n) && n >= 0 && n <= 60) setWithinMonths(n);
            }}
            min={0}
            max={60}
            step={1}
            allowDecimal={false}
            allowNegative={false}
            suffix=" months"
            style={{ width: 130 }}
          />
        </Group>
      </Group>
      <Text size="xs" c="dimmed">
        Today is <Text component="span" fw={600}>{reportQ.data?.today ?? '…'}</Text>.
        Set the window to 0 to show only records that are already overdue.
      </Text>

      {reportQ.data && filteredItems.length === 0 && (
        <Paper withBorder p="lg" radius="md">
          <Group gap="sm">
            <IconShieldCheck size={20} color="var(--mantine-color-verde-6)" />
            <Text size="sm">
              No records with dates expiring within the next {withinMonths} month{withinMonths === 1 ? '' : 's'}{filterType ? ` (filtered to ${ENTITY_TYPE_LABEL[filterType]})` : ''}.
            </Text>
          </Group>
        </Paper>
      )}

      {reportQ.data && filteredItems.length > 0 && (
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <SortableTh label="Type" col="type" sort={sort} w={120}
                onSort={(k) => setSort((s) => nextSort(s, k))} filtered={filterType !== ''} />
              <SortableTh label="Record" col="record" sort={sort} w={200}
                onSort={(k) => setSort((s) => nextSort(s, k))} />
              <SortableTh label="Expiring dates" col="dates" sort={sort}
                onSort={(k) => setSort((s) => nextSort(s, k))} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedItems.map((r) => (
              <ExpiringRow key={`${r.entity_type}-${r.entity_id}`} record={r}
                onRowClick={() => navigate(r.detail_path)}
                onDateClick={(p) => navigate(p)} />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function ExpiringRow({
  record, onRowClick, onDateClick,
}: {
  record: ExpiringRecord;
  onRowClick: () => void;
  onDateClick: (path: string) => void;
}) {
  return (
    <Table.Tr style={{ cursor: 'pointer' }}>
      <Table.Td onClick={onRowClick}>
        <Badge variant="light">{ENTITY_TYPE_LABEL[record.entity_type]}</Badge>
      </Table.Td>
      <Table.Td onClick={onRowClick}>
        <Text fw={500}>{record.label}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="wrap">
          {record.dates.map((d) => (
            <ExpiringDatePill key={d.kind} info={d}
              onClick={(e) => { e.stopPropagation(); onDateClick(d.detail_path); }} />
          ))}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function ExpiringDatePill({
  info, onClick,
}: { info: ExpiringDateInfo; onClick: (e: React.MouseEvent) => void }) {
  return (
    <Tooltip label="Click to open the relevant tab" withArrow openDelay={300}>
      <Anchor
        onClick={onClick}
        underline="never"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
      >
        <Badge size="xs" variant="light" color="azul"
          styles={{ root: { cursor: 'pointer' } }}>
          {DATE_KIND_LABEL[info.kind]}
        </Badge>
        <Text size="sm" ff="monospace">{info.expires_on}</Text>
        {statusBadge(info.days_until_due)}
      </Anchor>
    </Tooltip>
  );
}
